import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, User as UserIcon, BarChart3, FileText, Upload, Download, FileSpreadsheet, Edit, Eye, BookOpen, MessageSquareQuote, Users, Instagram, Book, PenTool, Mail, Link, DollarSign, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Footer from '@/components/Footer';

const AdminDashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calls');
  const [rejectReason, setRejectReason] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [generatingArt, setGeneratingArt] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }
    
    // Not logged in
    if (!user) {
      navigate('/admin-login');
      return;
    }
    
    // Not admin
    if (user.role !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem acessar.');
      navigate('/dashboard');
      return;
    }
    
    fetchData();
  }, [activeTab, user, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'suggestions') {
        const response = await api.get('/api/call-suggestions/pending');
        setSuggestions(response.data.suggestions);
      } else if (activeTab === 'verifications') {
        const response = await api.get('/api/editor-verification/pending');
        setVerifications(response.data.verifications);
      } else if (activeTab === 'calls') {
        const response = await api.get('/api/calls');
        setCalls(response.data.calls || []);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor, selecione um arquivo Excel (.xlsx ou .xls)');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/admin/calls/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadResult(response.data);
      
      if (response.data.created_count > 0) {
        toast.success(`${response.data.created_count} chamadas cadastradas com sucesso!`);
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erros encontrados`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar arquivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    // Create a simple CSV template that can be opened in Excel
    const headers = ['titulo', 'descricao', 'revista', 'issn', 'qualis', 'areas', 'prazo', 'fluxo_continuo', 'gratuito', 'url'];
    const example = [
      'Chamada para Dossiê: Exemplo',
      'Descrição da chamada...',
      'Nome da Revista',
      '1234-5678',
      'A2',
      'Ciências Exatas e da Terra, Engenharias',
      '2025-06-30',
      'Não',
      'Sim',
      'https://exemplo.com'
    ];
    
    const csvContent = headers.join(',') + '\n' + example.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template_chamadas.csv';
    link.click();
  };

  const approveSuggestion = async (suggestionId) => {
    setProcessingId(suggestionId);
    try {
      await api.post(`/api/call-suggestions/${suggestionId}/approve`);
      toast.success('Sugestão aprovada e chamada publicada!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao aprovar sugestão');
    } finally {
      setProcessingId(null);
    }
  };

  const rejectSuggestion = async (suggestionId) => {
    if (!rejectReason.trim()) {
      toast.error('Informe o motivo da rejeição');
      return;
    }

    setProcessingId(suggestionId);
    try {
      await api.post(`/api/call-suggestions/${suggestionId}/reject`, null, {
        params: { reason: rejectReason }
      });
      toast.success('Sugestão rejeitada');
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error('Erro ao rejeitar sugestão');
    } finally {
      setProcessingId(null);
    }
  };

  const generateInstagramArt = async (callId, callTitle) => {
    setGeneratingArt(callId);
    toast.info('Gerando arte para Instagram... Aguarde alguns segundos.');
    
    try {
      const response = await api.post(`/api/instagram-art/generate/${callId}`, {}, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `instagram_${callId}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Arte gerada e baixada com sucesso!');
    } catch (error) {
      console.error('Error generating art:', error);
      toast.error('Erro ao gerar arte. Tente novamente.');
    } finally {
      setGeneratingArt(null);
    }
  };

  const [generatingSlugs, setGeneratingSlugs] = useState(false);

  const generateMissingSlugs = async () => {
    setGeneratingSlugs(true);
    try {
      const response = await api.post('/api/admin/generate-missing-slugs');
      const count = response.data.updated_calls?.length || 0;
      if (count > 0) {
        toast.success(`${count} slugs gerados com sucesso!`);
      } else {
        toast.info('Todas as chamadas já possuem slug.');
      }
    } catch (error) {
      toast.error('Erro ao gerar slugs');
    } finally {
      setGeneratingSlugs(false);
    }
  };

  const approveVerification = async (verificationId) => {
    setProcessingId(verificationId);
    try {
      await api.post(`/api/editor-verification/${verificationId}/approve`);
      toast.success('Editor verificado com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao aprovar verificação');
    } finally {
      setProcessingId(null);
    }
  };

  const rejectVerification = async (verificationId) => {
    if (!rejectReason.trim()) {
      toast.error('Informe o motivo da rejeição');
      return;
    }

    setProcessingId(verificationId);
    try {
      await api.post(`/api/editor-verification/${verificationId}/reject`, null, {
        params: { reason: rejectReason }
      });
      toast.success('Verificação rejeitada');
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error('Erro ao rejeitar verificação');
    } finally {
      setProcessingId(null);
    }
  };

  const getQualisClass = (qualis) => `qualis-${qualis.toLowerCase()}`;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002FA7] mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  // If not admin after loading, don't render (redirect will happen)
  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Painel do Administrador</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/users')}
            >
              <Users className="h-4 w-4 mr-2" />
              Usuários
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/metrics')}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Métricas
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/content')}
            >
              <FileText className="h-4 w-4 mr-2" />
              Conteúdos
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/testimonials')}
            >
              <MessageSquareQuote className="h-4 w-4 mr-2" />
              Depoimentos
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/books')}
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              <Book className="h-4 w-4 mr-2" />
              Livros Afiliados
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/blog')}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              <PenTool className="h-4 w-4 mr-2" />
              Blog
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/emails')}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <Mail className="h-4 w-4 mr-2" />
              Lista de Emails
            </Button>
            <Button
              variant="outline"
              onClick={generateMissingSlugs}
              disabled={generatingSlugs}
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              data-testid="generate-slugs-btn"
            >
              <Link className="h-4 w-4 mr-2" />
              {generatingSlugs ? 'Gerando...' : 'Gerar Slugs SEO'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/donations')}
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Doações IA
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/reengagement')}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reengajamento
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-[#E4E4E7]">
          <button
            data-testid="calls-tab"
            onClick={() => setActiveTab('calls')}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'calls'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-[#4B4C52] hover:text-[#0A0A0A]'
            }`}
          >
            <BookOpen className="h-4 w-4" />
            Chamadas ({calls.length})
          </button>
          <button
            data-testid="suggestions-tab"
            onClick={() => setActiveTab('suggestions')}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
              activeTab === 'suggestions'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-[#4B4C52] hover:text-[#0A0A0A]'
            }`}
          >
            Sugestões ({suggestions.length})
          </button>
          <button
            data-testid="verifications-tab"
            onClick={() => setActiveTab('verifications')}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
              activeTab === 'verifications'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-[#4B4C52] hover:text-[#0A0A0A]'
            }`}
          >
            Verificações ({verifications.length})
          </button>
          <button
            data-testid="bulk-upload-tab"
            onClick={() => setActiveTab('bulk')}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'bulk'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-[#4B4C52] hover:text-[#0A0A0A]'
            }`}
          >
            <FileSpreadsheet className="h-4 w-4" />
            Importar Excel
          </button>
        </div>

        {loading && activeTab !== 'bulk' ? (
          <div className="text-center py-12">Carregando...</div>
        ) : activeTab === 'calls' ? (
          /* Calls Management Section */
          <div className="space-y-4">
            {/* Search */}
            <div className="bg-white border border-[#E4E4E7] rounded-md p-4">
              <Input
                placeholder="Buscar chamadas por título ou revista..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
            </div>

            {/* Calls List */}
            {calls
              .filter(call => 
                call.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                call.journal_name?.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .length === 0 ? (
              <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-[#4B4C52]">Nenhuma chamada encontrada</p>
              </div>
            ) : (
              calls
                .filter(call => 
                  call.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  call.journal_name?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(call => (
                  <div key={call.call_id} className="bg-white border border-[#E4E4E7] rounded-md p-4 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge className={getQualisClass(call.qualis_classification)}>
                            QUALIS {call.qualis_classification}
                          </Badge>
                          {call.continuous_flow ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">Fluxo Contínuo</Badge>
                          ) : call.deadline && (
                            <Badge variant="outline">Prazo: {formatDate(call.deadline)}</Badge>
                          )}
                          {call.status === 'archived' && (
                            <Badge variant="outline" className="bg-gray-100 text-gray-600">Arquivada</Badge>
                          )}
                        </div>
                        <h3 className="font-bold text-lg">{call.title}</h3>
                        <p className="text-sm text-[#4B4C52]">{call.journal_name}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/call/${call.call_id}`, '_blank')}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/admin/call/${call.call_id}`)}
                          className="bg-[#002FA7] hover:bg-[#001f75]"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateInstagramArt(call.call_id, call.title)}
                          disabled={generatingArt === call.call_id}
                          className="border-pink-300 text-pink-600 hover:bg-pink-50"
                          title="Gerar arte para Instagram"
                        >
                          {generatingArt === call.call_id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-pink-500 border-t-transparent rounded-full" />
                          ) : (
                            <Instagram className="h-4 w-4" />
                          )}
                          <span className="ml-1 hidden sm:inline">Arte</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        ) : activeTab === 'bulk' ? (
          /* Bulk Upload Section */
          <div className="space-y-6">
            <div className="bg-white border border-[#E4E4E7] rounded-md p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Importar Chamadas em Massa
              </h2>
              <p className="text-[#4B4C52] mb-6">
                Faça upload de um arquivo Excel (.xlsx) com as chamadas para cadastrar automaticamente.
              </p>

              {/* Template Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Formato do Arquivo</h3>
                <p className="text-sm text-blue-800 mb-3">
                  O arquivo deve conter as colunas: <strong>titulo</strong> (obrigatório), descricao, revista, <strong>issn</strong>, qualis, areas, prazo, fluxo_continuo, gratuito, url
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Baixar Modelo CSV
                </Button>
              </div>

              {/* File Upload */}
              <div className="border-2 border-dashed border-[#E4E4E7] rounded-lg p-8 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="excel-upload"
                />
                <FileSpreadsheet className="h-12 w-12 mx-auto text-[#4B4C52] mb-4" />
                <p className="text-[#4B4C52] mb-4">Arraste um arquivo Excel ou clique para selecionar</p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#002FA7] hover:bg-[#001f75]"
                >
                  {uploading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Processando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Selecionar Arquivo
                    </>
                  )}
                </Button>
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className="mt-6 space-y-4">
                  <div className={`p-4 rounded-md ${uploadResult.created_count > 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {uploadResult.created_count > 0 ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      )}
                      <span className="font-semibold">
                        {uploadResult.created_count} de {uploadResult.total_rows} chamadas cadastradas
                      </span>
                    </div>
                    {uploadResult.created_count > 0 && (
                      <p className="text-sm text-green-700">
                        As chamadas foram adicionadas e já estão disponíveis no sistema.
                      </p>
                    )}
                  </div>

                  {uploadResult.errors?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                        <XCircle className="h-4 w-4" />
                        Erros Encontrados
                      </h4>
                      <ul className="list-disc list-inside text-sm text-red-800 space-y-1">
                        {uploadResult.errors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                      {uploadResult.has_more_errors && (
                        <p className="text-sm text-red-700 mt-2">... e mais erros</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-white border border-[#E4E4E7] rounded-md p-6">
              <h3 className="font-bold mb-4">Instruções</h3>
              <ul className="space-y-2 text-sm text-[#4B4C52]">
                <li>• A coluna <strong>titulo</strong> é obrigatória</li>
                <li>• O <strong>issn</strong> é o código identificador da revista (formato: 0000-0000)</li>
                <li>• Use <strong>qualis</strong> com valores como: A1, A2, A3, A4, B1, B2, B3, B4, B5, C</li>
                <li>• Para <strong>areas</strong>, separe múltiplas áreas por vírgula</li>
                <li>• Use <strong>Sim/Não</strong> para fluxo_continuo e gratuito</li>
                <li>• O <strong>prazo</strong> pode ser no formato DD/MM/YYYY ou YYYY-MM-DD</li>
                <li>• Se fluxo_continuo = Sim, o prazo será ignorado</li>
              </ul>
            </div>
          </div>
        ) : activeTab === 'suggestions' ? (
          suggestions.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center">
              <p className="text-[#4B4C52]">Nenhuma sugestão pendente</p>
            </div>
          ) : (
            <div className="space-y-6">
              {suggestions.map(suggestion => (
                <div
                  key={suggestion.suggestion_id}
                  data-testid={`suggestion-${suggestion.suggestion_id}`}
                  className="bg-white border border-[#E4E4E7] rounded-md p-6"
                >
                  {suggestion.has_duplicate && (
                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md flex gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-orange-900">⚠️ Possível Duplicata</p>
                        <p className="text-sm text-orange-800">Verifique se já existe uma chamada similar antes de aprovar.</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold tracking-tight mb-2">{suggestion.title}</h3>
                      <p className="text-sm text-[#4B4C52] mb-2">{suggestion.journal_name}</p>
                      {suggestion.user_info && (
                        <div className="flex items-center gap-2 text-sm text-[#4B4C52]">
                          <UserIcon className="h-4 w-4" />
                          <span>{suggestion.user_info.name} ({suggestion.user_info.email})</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="text-sm mb-4">{suggestion.description}</p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge className={`${getQualisClass(suggestion.qualis_classification)} font-semibold`}>
                      QUALIS {suggestion.qualis_classification}
                    </Badge>
                    <Badge variant="outline">{suggestion.publication_type}</Badge>
                    {suggestion.is_free && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Gratuito
                      </Badge>
                    )}
                  </div>

                  <div className="text-sm text-[#4B4C52] mb-4">
                    <p>Prazo: {formatDate(suggestion.deadline)}</p>
                    <p>Áreas: {suggestion.knowledge_areas.join(', ')}</p>
                    {suggestion.submission_url && (
                      <p>URL: <a href={suggestion.submission_url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">{suggestion.submission_url}</a></p>
                    )}
                  </div>

                  <div className="border-t border-[#E4E4E7] pt-4 mt-4">
                    <div className="flex gap-3 items-start">
                      <div className="flex-1">
                        <Label className="text-sm">Motivo da rejeição (se aplicável)</Label>
                        <Input
                          data-testid={`reject-reason-${suggestion.suggestion_id}`}
                          placeholder="Ex: Chamada duplicada, informações incompletas..."
                          value={processingId === suggestion.suggestion_id ? rejectReason : ''}
                          onChange={(e) => {
                            setProcessingId(suggestion.suggestion_id);
                            setRejectReason(e.target.value);
                          }}
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <Button
                        data-testid={`approve-btn-${suggestion.suggestion_id}`}
                        onClick={() => approveSuggestion(suggestion.suggestion_id)}
                        disabled={processingId === suggestion.suggestion_id && rejectReason}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Aprovar e Publicar
                      </Button>
                      <Button
                        data-testid={`reject-btn-${suggestion.suggestion_id}`}
                        onClick={() => rejectSuggestion(suggestion.suggestion_id)}
                        disabled={!rejectReason || processingId !== suggestion.suggestion_id}
                        variant="destructive"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Rejeitar
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          verifications.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center">
              <p className="text-[#4B4C52]">Nenhuma verificação pendente</p>
            </div>
          ) : (
            <div className="space-y-6">
              {verifications.map(verification => (
                <div
                  key={verification.verification_id}
                  data-testid={`verification-${verification.verification_id}`}
                  className="bg-white border border-[#E4E4E7] rounded-md p-6"
                >
                  <div className="mb-4">
                    <h3 className="text-xl font-bold tracking-tight mb-2">Solicitação de Verificação</h3>
                    {verification.user_info && (
                      <div className="flex items-center gap-2 text-sm text-[#4B4C52]">
                        <UserIcon className="h-4 w-4" />
                        <span>{verification.user_info.name} ({verification.user_info.email})</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div>
                      <strong className="text-sm">URL do Documento:</strong>
                      <p className="text-sm">
                        <a href={verification.document_url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">
                          {verification.document_url}
                        </a>
                      </p>
                    </div>
                    <div>
                      <strong className="text-sm">URL da Revista:</strong>
                      <p className="text-sm">
                        <a href={verification.journal_url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">
                          {verification.journal_url}
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-[#E4E4E7] pt-4 mt-4">
                    <div className="flex gap-3 items-start mb-4">
                      <div className="flex-1">
                        <Label className="text-sm">Motivo da rejeição (se aplicável)</Label>
                        <Input
                          data-testid={`reject-reason-${verification.verification_id}`}
                          placeholder="Ex: Documentação inválida, não é editor..."
                          value={processingId === verification.verification_id ? rejectReason : ''}
                          onChange={(e) => {
                            setProcessingId(verification.verification_id);
                            setRejectReason(e.target.value);
                          }}
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        data-testid={`approve-verification-btn-${verification.verification_id}`}
                        onClick={() => approveVerification(verification.verification_id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Aprovar Editor
                      </Button>
                      <Button
                        data-testid={`reject-verification-btn-${verification.verification_id}`}
                        onClick={() => rejectVerification(verification.verification_id)}
                        disabled={!rejectReason || processingId !== verification.verification_id}
                        variant="destructive"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Rejeitar
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default AdminDashboardPage;
